import { Box, Grid, LinearProgress } from "@mui/material";
import ThemeContext from "components/app/Context";
import ExportButton from "components/common/Export";
import { exportURLs } from "components/common/Export/ExportAPIEndpoints";
import { filterPage } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import KeyMetrics from "components/common/KeyMetrics/KeyMetrics";
import BasicTable from "components/common/Table";
import Typography from "components/common/Typography";
import { isEmpty } from "lodash";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { DEFAULT_CORP_INNCODE, messages } from "utils/CommonConstants";
import {
  checkIsPlanCustomizedOrNot,
  filterPayloadToPlanProgressPayload,
  formFilterRequestPayload,
  getMembersList,
  getMoreValues,
  getOwnerNames,
  getSourceName,
  getTableColor
} from "utils/CommonFunctions";
import { getUIFormatDate } from "utils/DateHelper";
import { getPlanDetailsRoute, getRoleCategory } from "utils/StoreUtils";
import { serviceTypes } from "utils/Types";
import {
  fetchAbovePropertyMyPlansAsync,
  fetchAssigneeDetailsByHtlIdsAsync,
  fetchSnapshotData,
  setLoader
} from "../AboveProperty/AboveProperty.slice";

export const headers = [
  { content: "" },
  { content: "Property" },
  { content: "Customized" },
  { content: "Inncode" },
  { content: "Owner(s)" },
  { content: "Role Category" },
  { content: "Source" },
  { content: "Status" },
  { content: "Deadline" },
  { content: "Due Date" }
];

const PlanProgressList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const params = useParams();
  const { isMobileScreen } = useContext(ThemeContext);

  const [isMounted, setIsMounted] = useState(false);
  const [pageNo, setPageNo] = useState(0);

  const innCode = useAppSelector((state: any) => state.app.innCode);

  const { totalList, plansList: userplans, snapshotData } = useAppSelector((state) => state.aboveProperty.planProgress);
  const selectedPlanDetail: any = useAppSelector((state) => state.planDetails.planDetails);
  const page = filterPage.planProgressFromLibraryDisplay;
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[page]);
  const filterRequestPayload = globalFiltersforPage?.modifiedFilter || globalFiltersforPage?.defaultFilter || [];
  const formattedFilterRequestPayload = filterPayloadToPlanProgressPayload(
    formFilterRequestPayload(filterRequestPayload)
  );
  const searchText = globalFiltersforPage?.searchText;

  const getPlanProps = (startPage?: number) => {
    if (startPage === 0) setPageNo(startPage);
    const props = {
      innCode: getInncodeValue(),
      includeOwners: true,
      includeFilters: true,
      cmsId: params.id,
      searchedPropertyText: searchText,
      ...formattedFilterRequestPayload
    };

    const page = startPage ?? pageNo;
    const payload = { ...props };
    return { payload, page };
  };

  const getInncodeValue = () => {
    if (location?.state?.innCodes && !location?.state?.innCode) return location?.state?.innCodes.join(",");
    else if (location?.state?.innCode)
      return location?.state?.innCode === DEFAULT_CORP_INNCODE ? "" : location?.state?.innCode;
    else if (innCode === DEFAULT_CORP_INNCODE) return "";
    else return innCode;
  };

  const getSnapshotProps = () => {
    return {
      innCode: getInncodeValue(),
      includeOwners: true,
      cmsId: params.id,
      fetchSnapshot: true,
      ...formattedFilterRequestPayload
    };
  };

  const getPlanProgress = async (props: any) => {
    const plans = await dispatch(fetchAbovePropertyMyPlansAsync(props));
    const planOwners = plans?.payload?.meta?.owners;
    if (planOwners?.length) {
      const data = {
        value: planOwners.join(","),
        size: planOwners?.length
      };
      if (!isMounted) await dispatch(fetchAssigneeDetailsByHtlIdsAsync(data));
      setIsMounted(true);
    }
    dispatch(setLoader(false));
  };

  useEffect(() => {
    dispatch(fetchSnapshotData(getSnapshotProps()));
  }, [filterRequestPayload]);

  useEffect(() => {
    getPlanProgress(getPlanProps(0));
  }, [filterRequestPayload, searchText]);

  useEffect(() => {
    if (pageNo > 0) getPlanProgress(getPlanProps());
  }, [pageNo]);

  const getDateColor = (date: any, status: string) => {
    const todaysDate = moment().format("YYYY-MM-DD");
    return moment(date).isBefore(todaysDate) && status !== "Completed"
      ? tokens.Text.Feedback.Alert
      : tokens.Text.General.PrimaryHeadline;
  };

  const getMetricsData = () => {
    return [
      { isPrimary: true, subTitle: "New", data: snapshotData?.newMyPlansCount },
      { subTitle: "In Progress", data: snapshotData?.inprogressMyPlansCount, showBorder: true },
      { subTitle: "Completed", data: snapshotData?.completedMyPlansCount, showBorder: true },
      { subTitle: "Overdue", data: snapshotData?.overDueMyPlansCount }
    ];
  };

  return (
    <Grid
      sx={{
        padding: "24px",
        backgroundColor: "white",
        borderRadius: "8px"
      }}
    >
      <GlobalFilters
        searchBarPlaceholder="Search by Property"
        page={page}
        isSearchBarVisible={isEmpty(getInncodeValue())}
        searchedText={searchText}
        isPreDefinedNav={true}
        getMembersList={(searchValue: string) => getMembersList(searchValue, dispatch)}
      />
      <GlobalFilterDescription page={page} />

      <Grid display={"flex"} flexDirection={"column"} gap="20px" marginTop={isMobileScreen ? "10px" : "24px"}>
        <KeyMetrics keyMetricsData={getMetricsData()} />
        <Box display="flex" justifyContent={"flex-end"} marginRight={"16px"}>
          <ExportButton
            URL={exportURLs.individualProperty_plans}
            data={{
              innCode: getInncodeValue(),
              includeOwners: true,
              includeFilters: true,
              cmsId: params.id,
              searchedPropertyText: searchText,
              ...formattedFilterRequestPayload,
              view: "library",
              planName: selectedPlanDetail?.name
            }}
            serviceType={serviceTypes.userplan}
          />
        </Box>
        <BasicTable
          id="tableDiv"
          totalRecords={totalList}
          pagination={{ pageNo, setPageNo }}
          noDataMessage={messages.noPropertyPlans}
          data={{
            headers,
            rows: userplans?.map((element: any) => {
              const progressVal = element?.cardsCompleted / element?.totalCards;
              const route = getPlanDetailsRoute();
              return {
                data: [
                  {
                    content: (
                      <Box
                        onClick={() => navigate(`/${route}/${element?.id}`)}
                        sx={{
                          textDecoration: "none",
                          cursor: "pointer",
                          color: tokens.Text.General.PrimaryHeadline,
                          fontWeight: "700"
                        }}
                      >
                        {element?.propertyName ?? element?.innCode}
                      </Box>
                    ),
                    showBold: true
                  },
                  {
                    content: checkIsPlanCustomizedOrNot(element?.isPlanCustomized)
                  },
                  { content: element?.innCode },
                  {
                    content: getMoreValues(getOwnerNames(element?.owners)),
                    tooltip: {
                      title: "Owners",
                      list: getOwnerNames(element?.owners)?.map((owner: any) => {
                        return { name: owner };
                      })
                    }
                  },

                  { content: getRoleCategory(element?.owners) ?? "-" },
                  { content: getSourceName(element?.planSource) },

                  {
                    content: (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start"
                        }}
                      >
                        <Typography variant="body2" color={getTableColor(element?.status)}>
                          {" "}
                          {element?.status}
                        </Typography>
                        {element?.status !== "New" && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center"
                            }}
                          >
                            {" "}
                            <LinearProgress
                              sx={{ width: "80px", height: "6px" }}
                              variant="determinate"
                              value={!isNaN(progressVal) ? progressVal * 100 : 0}
                            />
                            <Typography component={"span"}>
                              {" "}
                              {!isNaN(progressVal) ? `${Math.round(progressVal * 100)}%` : `0%`}
                            </Typography>{" "}
                          </Box>
                        )}
                      </Box>
                    )
                  },

                  {
                    content: getUIFormatDate(element?.brandDeadlineDate),
                    color: moment(element?.brandDeadlineDate).isBefore(new Date()) ? tokens.Text.Feedback.Alert : ""
                  },
                  {
                    content: getUIFormatDate(element?.dueDate),
                    color: getDateColor(element?.dueDate, element?.status)
                  }
                ]
              };
            })
          }}
        />
      </Grid>
    </Grid>
  );
};
export default PlanProgressList;
